import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { pick, useLang } from "@/i18n";
import { PackageCard } from "@/components/PackageCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

export default function Experiences() {
    const { lang, t } = useLang();
    const [params, setParams] = useSearchParams();
    const active = params.get("cat") || "";
    const q = params.get("q") || "";
    const [categories, setCategories] = useState([]);
    const [packages, setPackages] = useState(null);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        api.get("/public/categories").then((r) => setCategories(r.data.experiences || [])).catch(() => {});
    }, []);

    useEffect(() => {
        setPackages(null);
        setLoadError(false);
        api.get("/public/packages", { params: { ...(active ? { category: active } : {}), ...(q ? { q } : {}) } })
            .then((r) => setPackages(r.data.filter((p) => (active ? true : !p.category.startsWith("transfer-")))))
            .catch(() => { setPackages([]); setLoadError(true); });
    }, [active, q]);

    return (
        <div data-testid="experiences-page" className="marble-bg min-h-screen pt-32 pb-24">
            <Seo lang={lang} title={`${t.experiences.title} — TOURVOYAGERS`} description={t.experiences.sub} />
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <Reveal>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium">TOURVOYAGERS</p>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-tight">{t.experiences.title}</h1>
                    <p className="mt-5 max-w-2xl text-base text-stone-300 leading-relaxed">{t.experiences.sub}</p>
                    {q && <p className="mt-3 text-sm text-amber-300/90" data-testid="experiences-search-label">«{q}»</p>}
                </Reveal>

                <div className="mt-10 flex flex-wrap gap-2" data-testid="experience-category-tabs">
                    <button
                        data-testid="experience-tab-all"
                        onClick={() => setParams(q ? { q } : {})}
                        className={`rounded-full border px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${!active ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/30 text-stone-300 hover:border-amber-400/60"}`}
                    >
                        {t.experiences.all}
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.slug}
                            data-testid={`experience-tab-${c.slug}`}
                            onClick={() => setParams({ ...(q ? { q } : {}), cat: c.slug })}
                            className={`rounded-full border px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors ${active === c.slug ? "border-amber-400 bg-amber-400 text-black" : "border-amber-500/30 text-stone-300 hover:border-amber-400/60"}`}
                        >
                            {c.label[lang] || c.label.it}
                        </button>
                    ))}
                </div>

                <div className="mt-14">
                    {packages === null ? (
                        <p className="text-sm text-stone-500">{t.common.loading}</p>
                    ) : loadError ? (
                        <div className="rounded-xl border border-amber-500/25 bg-[#121216] p-8 text-center" data-testid="catalog-error">
                            <p className="text-sm text-stone-300">{t.common.catalogError}</p>
                        </div>
                    ) : packages.length === 0 ? (
                        <p className="text-sm text-stone-500" data-testid="experiences-empty">{t.experiences.empty}</p>
                    ) : (
                        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                            {packages.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
